/**
 * MilestoneRow — compact roadmap row.
 *
 * Never a giant card: single line title + status pill + subtle meta,
 * plus optional secondary line for blocker / next / evidence.
 */
import { StatusPill } from "./StatusPill";
import { Target, AlertTriangle, ArrowRight, FileText, Edit3 } from "lucide-react";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return iso.slice(0, 10); }
}

export default function MilestoneRow({ milestone, isFounder, onEdit }) {
    const m = milestone;
    const hasSecondary = m.current_blocker || m.next_action || m.evidence_label || m.completed_at;
    return (
        <div
            data-testid={`gtm-milestone-row-${m.code}`}
            data-status={m.status}
            data-launch-critical={m.launch_critical ? "true" : "false"}
            className={`group relative rounded-xl border border-qoryn-border/60 bg-black/25 hover:bg-white/[0.02] transition-colors px-4 py-2.5 ${
                m.status === "LOCKED" || m.status === "DEFERRED" ? "opacity-60" : ""
            }`}
        >
            <div className="flex items-center gap-3 min-w-0">
                {m.launch_critical && (
                    <span
                        title="Launch-critical"
                        aria-label="Launch-critical"
                        className="shrink-0 text-qoryn-cyan"
                        data-testid={`gtm-launch-critical-${m.code}`}
                    >
                        <Target className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <div className="font-sans text-[13.5px] text-qoryn-text truncate">
                        {m.title}
                    </div>
                    {m.description && (
                        <div className="font-sans text-[11.5px] text-qoryn-textDim/90 truncate">
                            {m.description}
                        </div>
                    )}
                </div>
                <div className="shrink-0 flex items-center gap-2">
                    <StatusPill status={m.status} />
                    {isFounder && (
                        <button
                            type="button"
                            data-testid={`gtm-milestone-edit-${m.code}`}
                            onClick={() => onEdit?.(m)}
                            title="Update status"
                            className="h-7 w-7 flex items-center justify-center rounded-md text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.04] opacity-0 group-hover:opacity-100 transition-opacity qoryn-focus"
                        >
                            <Edit3 className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                    )}
                </div>
            </div>
            {hasSecondary && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 pl-6">
                    {m.completed_at && (
                        <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                            Completed {fmtDate(m.completed_at)}
                        </span>
                    )}
                    {m.current_blocker && (
                        <span data-testid={`gtm-blocker-${m.code}`} className="inline-flex items-center gap-1 font-mono-ui text-[9px] tracking-widest uppercase text-red-300/90">
                            <AlertTriangle className="h-3 w-3" strokeWidth={1.75} />
                            {m.current_blocker}
                        </span>
                    )}
                    {m.next_action && (
                        <span className="inline-flex items-center gap-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-cyan/80">
                            <ArrowRight className="h-3 w-3" strokeWidth={1.75} />
                            {m.next_action}
                        </span>
                    )}
                    {m.evidence_label && (
                        <span className="inline-flex items-center gap-1 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                            <FileText className="h-3 w-3" strokeWidth={1.75} />
                            {m.evidence_label}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
